import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { Router } from '@angular/router';
import { OverrideService } from '../../../@shelter/utils/override.service';
import { API_URLS } from '../../../@shelter/utils/API_URLS';
import { User } from '../../../@shelter/models/user.model';
import { UserType } from '../../../@shelter/enums/user-type.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
;

@Injectable()
export class AuthService {
  public _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _router: Router,
        private override: OverrideService,
        private _snackBar: MatSnackBar
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('token', token);
    }

    get accessToken(): string {
        return localStorage.getItem('token') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<User> {
        return this._httpClient.put<User>(API_URLS.Auth.changePassword, password).pipe(
            tap(res=>{
                //console.log(res);

                return of(res);
            } ),
            switchMap((response: User) => {

                // Store the access token in the local storage
                // this.accessToken = response.token;

                // Set the authenticated flag to true
                // this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response;

                // Return a new observable with the response
                return of(response);
            })
        );;
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string, defaultLang: string }): Observable<User> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }
         //console.log('credentials',credentials);

        return this._httpClient.post(API_URLS.Auth.login, credentials,{
            headers: this.override.AuthHeaders
        }).pipe(
            tap(res=>{
                //console.log(res);

                return of(res);
            } ),
            switchMap((response: any) => {
          
                     // Store the access token in the local storage
                this.accessToken = response.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response;

                // Return a new observable with the response
                return of(response);
                
               
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        //console.log("SigninWIth token method");
        
        // Renew token
        return this._httpClient.post(API_URLS.Auth.loginByToken,null).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {
                if(!response.isActive) {
                    this.signOut()
                    this._router.navigate(['/sign-in'])
                    this._snackBar.open('User is deactivated','',{ duration: 5000 });
                    return of(false);
                }

                // Store the access token in the local storage
                // this.accessToken = response.user.token;
                else {
                     // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response;
                return of(true);

                }            
                // Return true
               // return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('token');
        localStorage.removeItem('userType')
        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            //console.log("Is authed");
            
            return of(true);
        }
        
        // Check the access token availability
        if (!this.accessToken) {
            //console.log("not access token");
            return of(false);
        }
        
        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            //console.log("Expired");
            return of(false);
        }
        
        // If the access token exists and it didn't expire, sign in using it
        //console.log("Sign in with token");
        return this.signInUsingToken();
    }
}
